// src/components/dashboard/Add_Address.jsx
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  get_all_addresses,
  get_selected_address,
  add_new_address,
  update_shipping_address,
set_selected_address
} from '../../store/reducers/customerShippingReducer'
import Headers from '../Headers'
import Footer from '../Footer'
import LocationSelector from '../LocationSelector'
import {
 FiEdit2,
  FiPlus,
  FiCheck,
  FiX,
  FiCheckCircle,
  FiCircle
} from 'react-icons/fi'

const addressFields = [
  { label: 'Save As', name: 'saveAs', placeholder: 'Home, Office…' },
  { label: 'Name',    name: 'name',    placeholder: 'Full Name' },
  { label: 'Address', name: 'address', placeholder: 'House no / street / area' },
  { label: 'Phone',   name: 'phone',   placeholder: '10-digit phone' },
  { label: 'Post',    name: 'post',    placeholder: 'Postal code' }
]

export default function Add_Address() {
  const dispatch = useDispatch()
  const { userInfo } = useSelector(s => s.auth)
  const {
    shippingAddresses,
    selectedAddress,
    loading,
    error
  } = useSelector(s => s.customer)

  const [showForm, setShowForm] = useState(false)
  const [editId,    setEditId]    = useState(null)
  const [form,      setForm]      = useState(
    addressFields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {
      province: '',
      city: '',
      area: ''
    })
  )

  // ─── load addresses + current selection ───
  useEffect(() => {
    if (userInfo?.id) {
      dispatch(get_all_addresses(userInfo.id))
      dispatch(get_selected_address(userInfo.id))
    }
  }, [dispatch, userInfo])

  const onChange = e => {
    const { name, value } = e.target
    setForm(f => ({ ...f, [name]: value }))
  }

  const startAdd = () => {
    setEditId(null)
    setForm(
      addressFields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {
        province: '',
        city: '',
        area: ''
      })
    )
    setShowForm(true)
  }

  const startEdit = addr => {
    setEditId(addr._id)
    setForm({
      saveAs:   addr.saveAs   || '',
      name:     addr.name     || '',
      address:  addr.address  || '',
      phone:    addr.phone    || '',
      post:     addr.post     || '',
      province: addr.province || '',
      city:     addr.city     || '',
      area:     addr.area     || ''
    })
    setShowForm(true)
  }

  const onCancel = () => {
    setShowForm(false)
    setEditId(null)
  }

  const onSubmit = e => {
    e.preventDefault()
    const addressData = { ...form }
    const thunk = editId
      ? update_shipping_address({ addressId: editId, addressData })
      : add_new_address({ customerId: userInfo.id, addressData })

    dispatch(thunk)
      .unwrap()
      .then(() => {
        setShowForm(false)
        setEditId(null)
        dispatch(get_all_addresses(userInfo.id))
      })
      .catch(console.log)
  }

  const handleSelect = addrId => {
    dispatch(set_selected_address({
      customerId: userInfo.id,
      addressId:  addrId
    }))
  }

  return (
    <>
      <Headers />
      <div className="p-6 bg-slate-100 min-h-[80vh]">
        <div className="bg-white rounded-md p-6 space-y-4">
          <h2 className="text-xl font-semibold">Manage Addresses</h2>
          {loading && <p>Loading…</p>}
          {error   && <p className="text-red-500">{error}</p>}

          <ul className="space-y-3">
            {shippingAddresses.map(addr => (
              <li
                key={addr._id}
                className="border rounded p-4 flex justify-between items-center"
              >
                <div className="flex items-center gap-4">
                  {/* ← compare by selectedAddress._id, not object */}
                  {selectedAddress?._id === addr._id
                    ? <FiCheckCircle className="text-green-500 cursor-pointer" />
                    : <FiCircle
                        className="cursor-pointer"
                        onClick={() => handleSelect(addr._id)}
                      />
                  }
                  <div className="space-y-1 text-sm">
                    <h3 className="font-medium">{addr.saveAs}</h3>
                    <p>{addr.name}</p>
                    <p>
                      {addr.address}, {addr.province}, {addr.city}, {addr.area}
                    </p>
                    <p>Post: {addr.post}</p>
                    <p>Phone: {addr.phone}</p>
                  </div>
                </div>
                <button
                  onClick={() => startEdit(addr)}
                  className="text-slate-400 hover:text-indigo-600"
                >
                  <FiEdit2 />
                </button>
              </li>
            ))}
          </ul>

          <button
            onClick={startAdd}
            className="inline-flex items-center gap-1 text-indigo-600 mt-4"
          >
            <FiPlus /> Add New Address
          </button>

          {showForm && (
            <form
              onSubmit={onSubmit}
              className="grid grid-cols-2 gap-4 mt-4 bg-slate-50 p-4 rounded-md shadow"
            >
              {addressFields.map(f => (
                <div key={f.name} className="flex flex-col">
                  <label
                    htmlFor={f.name}
                    className="text-sm text-slate-700 mb-1"
                  >
                    {f.label} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={f.name}
                    name={f.name}
                    value={form[f.name]}
                    onChange={onChange}
                    placeholder={f.placeholder}
                    {...(f.name === 'phone' ? { minLength: 10, maxLength: 10 } : {})}
                    {...(f.name === 'post'  ? { minLength: 6,  maxLength: 6 } : {})}
                    className="border border-slate-300 rounded px-2 py-1 focus:outline-none"
                  />
                </div>
              ))}

              <div className="col-span-2">
                <LocationSelector
                  values={form}
                  onChange={onChange}
                  errors={{}}
                />
              </div>

              <div className="col-span-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-3 py-1 rounded bg-red-500 text-white flex items-center gap-1"
                >
                  <FiX /> Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1 rounded bg-indigo-600 text-white flex items-center gap-1"
                >
                  <FiCheck /> {editId ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </>
  )
}
